/* eslint-disable no-undef */

interface RequestInterceptor {
  ():
    | Promise<{ headers?: Record<string, string> } | undefined>
    | { headers?: Record<string, string> }
    | undefined
}

interface ResponseInterceptor {
  (response: Response): Promise<any>
}

interface HttpClientOptions {
  baseURL?: string
  headers?: Record<string, string>
  interceptors?: {
    request?: RequestInterceptor
    response?: ResponseInterceptor
  }
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  formData?: boolean
  body?: any
  headers?: Record<string, string>
}

/**
 * Base REST Http Client wrapper arround the native fetch api
 *
 * @namespace Services
 * @see https://developer.mozilla.org/fr/docs/Web/API/Fetch_API/Using_Fetch
 *
 * @example <caption>Usage</caption>
 * const client = new HttpClient({ baseURL: 'https://localhost.com' })
 * client.get('/users')
 * client.post('/users', userInput)
 * client.delete('/users/1')
 */
class HttpClient {
  private _baseURL: string
  private _headers: Record<string, string>
  private _interceptors: {
    request?: RequestInterceptor
    response?: ResponseInterceptor
  }

  constructor(options: HttpClientOptions | string = {}) {
    this._baseURL =
      typeof options === 'string' ? options : options.baseURL || ''

    this._headers = {
      'Content-Type': 'application/json',
      ...(typeof options === 'object' ? options.headers : {}),
    }
    this._interceptors = {}
    if (typeof options === 'object') {
      this._interceptors.request = options?.interceptors?.request
      this._interceptors.response = options?.interceptors?.response
    }
  }

  async _fetchJSON(endpoint: string, options: FetchOptions = {}) {
    try {
      const interceptedOpts = await this._interceptors?.request?.()

      const headers: Record<string, string> = {
        ...this._headers,
        ...options?.headers,
        ...interceptedOpts?.headers,
      }

      const mergedOpts: RequestInit = {
        ...options,
        ...interceptedOpts,
        headers,
      }

      const isJSON = headers['Content-Type'] === 'application/json'

      if (options.body instanceof FormData) {
        // If body is already FormData, use it directly
        mergedOpts.body = options.body
        delete headers['Content-Type']
      } else if (options.body && options.formData) {
        const formData = new FormData()
        const body = options.body as Record<string, any>

        Object.keys(body).forEach((k) => {
          formData.append(k, body[k])
        })

        mergedOpts.body = formData
        delete headers['Content-Type']
      } else if (options.body && isJSON) {
        mergedOpts.body = JSON.stringify(options.body)
      }

      if (!mergedOpts.body) {
        delete mergedOpts.body
      }

      const res = await fetch(this._baseURL + (endpoint || ''), mergedOpts)

      if (this._interceptors?.response) {
        return this._interceptors.response(res)
      }

      if (!res.ok) throw new Error(res.statusText)

      if (isJSON && res.status !== 204) return await res.json()

      return res
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      console.error(error)
    }
  }

  async downloadFile(
    endpoint: string,
    options: RequestInit & { returnBlob?: boolean } = {}
  ) {
    try {
      const { returnBlob, ...fetchOptions } = options
      const interceptedOpts = await this._interceptors?.request?.()

      const mergedOpts: RequestInit = {
        ...fetchOptions,
        ...interceptedOpts,
        headers: {
          ...this._headers,
          ...fetchOptions?.headers,
          ...interceptedOpts?.headers,
        },
      }

      let filename = ''

      const response = await fetch(this._baseURL + (endpoint || ''), mergedOpts)

      if (!response.ok) throw new Error(response.statusText)

      const contentDisposition = response.headers.get('Content-Disposition')

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/)

        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1])
        } else {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)

          if (filenameMatch) {
            filename = filenameMatch[1]
          }
        }
      }

      const blob = await response.blob()

      // If returnBlob is true, just return the blob without triggering download
      if (returnBlob) {
        return blob
      }

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.style.display = 'none'
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  }

  get(endpoint: string, options: FetchOptions = {}) {
    return this._fetchJSON(endpoint, { ...options, method: 'GET' })
  }

  post(endpoint: string, body: any, options: FetchOptions = {}) {
    return this._fetchJSON(endpoint, { ...options, body, method: 'POST' })
  }

  put(endpoint: string, body: any, options: FetchOptions = {}) {
    return this._fetchJSON(endpoint, { ...options, body, method: 'PUT' })
  }

  patch(endpoint: string, body: any, options: FetchOptions = {}) {
    return this._fetchJSON(endpoint, { ...options, body, method: 'PATCH' })
  }

  delete(endpoint: string, body: any = {}, options: FetchOptions = {}) {
    return this._fetchJSON(endpoint, { ...options, body, method: 'DELETE' })
  }
}

export default HttpClient
